import { Sku, Variant } from '@v2/shared/lib/types';

export type VariantOption = {
  value: number | string;
  text: string;
  /** No sku exists with this option alongside the ones already chosen. */
  disabled: boolean;
  variant: Variant;
  selected: boolean;
};

export type VariantSelector = {
  id: number;
  name: string;
  options: VariantOption[];
};

/**
 * The format / term chooser, derived from the skus rather than declared anywhere.
 *
 * A sku carries its variants — "Format: Paperback", "Term: 1 Year" — and the set of
 * selectors is whatever names appear across the skus on offer. So a title sold only in
 * paperback produces one selector with one option, which is how the page knows not to
 * draw a chooser at all.
 *
 * Ported from headless_jshop's lib/variants.ts, which is where this shape was worked
 * out, and kept beside the page because nothing else has skus with variants yet.
 */
export function findSelectedVariant(typeId: number, variants: Variant[]): Variant | undefined {
  return variants.find((variant) => variant.type_id === typeId);
}

/**
 * The sku matching a set of variants, optionally swapping one of them out — which is
 * the question every option in the chooser asks: "if I picked this instead, what would
 * I get?".
 *
 * The override replaces by `name`, not by `type_id`: the same conceptual variant can
 * carry a different type id across two products in a group, and it is the name that is
 * stable between them.
 */
export function findSkuByVariants(skus: Sku[], variants: Variant[], variantOverride?: Variant): Sku | undefined {
  const wanted = variantOverride
    ? variants.filter((variant) => variant.name !== variantOverride.name).concat([variantOverride])
    : variants;

  return skus.find(
    (sku) =>
      wanted.filter((variant) =>
        sku.variants.find((skuVariant) => skuVariant.type_id === variant.type_id && skuVariant.id === variant.id)
      ).length === sku.variants.length
  );
}

export function buildVariantSelectors(skus: Sku[], selectedSku: Sku): VariantSelector[] {
  const selectors: Record<string, VariantSelector> = {};
  const seen: Record<string, number[]> = {};

  skus.forEach((sku) => {
    sku.variants.forEach((variant) => {
      selectors[variant.name] ||= { id: variant.type_id, name: variant.name, options: [] };
      seen[variant.name] ||= [];

      if (seen[variant.name].includes(variant.id)) {
        return;
      }

      seen[variant.name].push(variant.id);

      selectors[variant.name].options.push({
        value: variant.id,
        text: variant.content,
        disabled: !findSkuByVariants(
          skus,
          selectedSku.variants.filter((selected) => selected.name !== variant.name).concat([variant])
        ),
        variant,
        selected: findSelectedVariant(variant.type_id, selectedSku.variants)?.id === variant.id,
      });
    });
  });

  return Object.values(selectors);
}
